'use client';

import type { PostHog } from 'posthog-js';
import type { EventName, PropsFor } from './analytics-events';
import { deferredClient } from './deferred-client';

/**
 * PostHog, fetched by initAnalytics rather than imported here: about 76 KB
 * gzipped that every page used to load for every visitor, though it starts
 * only once the visitor accepts analytics cookies
 * (src/lib/first-load-bundle.test.ts). Until then every call below is dropped;
 * while it downloads, calls wait and run in order (src/lib/deferred-client.ts).
 */
const posthog = deferredClient<PostHog>();

// Query params that are bearer credentials and must never be shipped to
// analytics. The payment_intent_client_secret rides on the
// /receipt URL and doubles as the guest download/receipt proof, so a captured
// $current_url would otherwise let anyone with PostHog read access mint download
// tokens. `token`/`access_token` cover the magic-link + email flows.
const SENSITIVE_QUERY_PARAMS = [
  'payment_intent_client_secret',
  'setup_intent_client_secret',
  'client_secret',
  'token',
  'access_token',
];

function scrubUrlString(value: string): string {
  const qIndex = value.indexOf('?');
  if (qIndex === -1) return value;
  try {
    const params = new URLSearchParams(value.slice(qIndex + 1));
    let changed = false;
    for (const key of SENSITIVE_QUERY_PARAMS) {
      if (params.has(key)) { params.set(key, 'REDACTED'); changed = true; }
    }
    if (!changed) return value;
    const base = value.slice(0, qIndex);
    const rebuilt = params.toString();
    return rebuilt ? `${base}?${rebuilt}` : base;
  } catch {
    // Unparseable query — drop everything after '?' rather than risk leaking it.
    return value.slice(0, qIndex);
  }
}

// Redact secrets from every URL-bearing property on every captured event
// (pageview, pageleave, autocapture, exceptions). Single choke point so no
// capture path can leak a credential-carrying URL.
function sanitizeAnalyticsProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  const urlKeys = [
    '$current_url', '$referrer', '$pathname',
    '$initial_current_url', '$initial_referrer', '$initial_pathname',
  ];
  for (const key of urlKeys) {
    const v = properties[key];
    if (typeof v === 'string' && v.includes('?')) properties[key] = scrubUrlString(v);
  }
  return properties;
}

// Properties attached to every event, held here until PostHog starts (it only
// starts once the visitor accepts analytics cookies).
const pendingSuperProperties: Record<string, string | null> = {};

export function initAnalytics() {
  if (typeof window === 'undefined') return;
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!key) return;

  const host = process.env.NEXT_PUBLIC_POSTHOG_HOST ?? '/ingest';

  posthog.start(() => import('posthog-js').then(({ default: ph }) => {
    ph.init(key, {
      api_host: host,
      person_profiles: 'identified_only',
      capture_pageview: false,
      capture_pageleave: true,
      autocapture: false,
      // Strip bearer credentials from URL properties before anything is sent.
      sanitize_properties: (properties) => sanitizeAnalyticsProperties(properties),
      persistence: 'memory',
      disable_session_recording: true,
      capture_exceptions: true,
      // PostHog lazy-loads several optional autocapture chunks. Disable the
      // ones we don't use so they stop appearing as "missing source map"
      // warnings in Lighthouse and so the network tab is quieter.
      disable_surveys: true,
      capture_performance: false,
      // dead-clicks autocapture defaults to off but the chunk loads anyway
      // when autocapture is otherwise on; setting it explicitly keeps the
      // intent visible.
      capture_dead_clicks: false,
    });
    if (Object.keys(pendingSuperProperties).length) ph.register(pendingSuperProperties);
    return ph;
  }));
}

/**
 * The visitor's country as the SERVER resolved it (src/lib/geo-ip.ts, the same
 * lookup the VAT qualifier uses), sent as `visitor_country` on every event.
 * PostHog's own geolocation sees the request after the /ingest rewrite, so it
 * cannot be relied on to see the visitor's IP; the staff analytics dashboard
 * groups countries by this property instead. '' means unresolved.
 */
export function setVisitorCountry(country: string) {
  pendingSuperProperties.visitor_country = country || null;
  posthog.run((ph) => ph.register({ visitor_country: pendingSuperProperties.visitor_country }));
}

/** Called when the user grants analytics consent. Initialises PostHog if
 *  needed and re-enables capture (it may have been opted out from a prior
 *  session that revoked consent). */
export function optInAnalytics() {
  initAnalytics();
  posthog.run((ph) => ph.opt_in_capturing());
}

/** Called when the user denies or revokes analytics consent. Safe to call
 *  before init — there's just nothing to stop. */
export function optOutAnalytics() {
  posthog.run((ph) => ph.opt_out_capturing());
}

export function track<N extends EventName>(name: N, props: PropsFor<N>) {
  posthog.run((ph) => ph.capture(name, props as Record<string, unknown>));
}

export function identify(uid: string, traits?: Record<string, string | number | boolean | null>) {
  posthog.run((ph) => ph.identify(uid, traits));
}

export function reset() {
  posthog.run((ph) => ph.reset());
}

export function pageView(url: string) {
  posthog.run((ph) => ph.capture('$pageview', { $current_url: url }));
}

export function captureException(error: unknown, extra?: Record<string, unknown>) {
  posthog.run((ph) => ph.captureException(error, extra));
}
